import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetcher, useSearchParams } from 'react-router';
import type { FetcherData } from 'types';

import ErrorIcon from '~/assets/icons/Error';
import VectorIcon from '~/assets/icons/Vector';
import { Button } from '~/components/Button';
import TextInput from '~/components/Form/TextInput';
import CompleteRegistrationEvent from '~/events/CompleteRegistrationEvent';
import useQsParams from '~/hooks/useQsParams';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import { clearPendingRegistrationEmail } from '~/utils/browser';

import { Dialog, DialogDismiss } from '../Dialog';
import Modals from '.';
import ModalHeadings from './Login/ModalHeadings';

export type PostSignUpOptions = {
  action: string;
  legacyProductId: string;
};

const ErrorMessage = ({
  isActive,
  text
}: {
  isActive: boolean;
  text: string;
}) => {
  return isActive ? (
    <div className="flex h-5 flex-row content-center items-center gap-1">
      <ErrorIcon />
      <p className="text-xs font-bold text-red-600">{text}</p>
    </div>
  ) : (
    <></>
  );
};

export const EmailConfirmation = () => {
  const user = useUser();
  const { isBot, clientIp } = useRootData();
  const fetcher = useFetcher<FetcherData>();
  const qsParams = useQsParams();
  const { t } = useTranslation();

  const [, setSearchParams] = useSearchParams();
  const [showModal, setShowModal] = useState(
    Boolean(!user.isLoggedIn && qsParams.fromRegistration)
  );
  const [showAppDownload, setShowAppDownload] = useState(false);

  const errors = fetcher.data?.errors;
  const success = fetcher.data?.success;
  const idle = fetcher.state === 'idle';

  const isConfirmationComplete = success && idle;

  useEffect(() => {
    if (!isConfirmationComplete) return;

    clearPendingRegistrationEmail();

    setShowModal(false);

    if (
      fetcher.data?.completedAction === 'restock' &&
      qsParams.fromRegistration
    ) {
      setShowAppDownload(true);
    }

    if (user.isLoggedIn) {
      new CompleteRegistrationEvent({ clientIp, isBot, user }).send();
    }
  }, [
    isConfirmationComplete,
    user,
    clientIp,
    fetcher.data?.completedAction,
    qsParams.fromRegistration,
    isBot
  ]);

  function onDismiss() {
    setSearchParams((params) => {
      params.delete('fromRegistration');
      params.delete('token');
      params.delete('action');
      params.delete('guest_id');
      params.delete('lid');
      params.delete('from_nudge');
      params.delete('client');
      params.delete('medium');
      params.delete('source');

      return params;
    });

    setShowModal(false);
    setShowAppDownload(false);
  }

  if (showAppDownload) {
    return <Modals.AppDownload isOpen={true} onDismiss={onDismiss} />;
  }

  return showModal ? (
    <Dialog
      onDismiss={onDismiss}
      isOpen={true}
      className="z-50 my-4 flex w-96 max-w-[90%] flex-col gap-4 rounded-lg border border-white bg-black px-4 py-8"
    >
      <nav className="flex flex-row-reverse justify-between">
        <DialogDismiss className="text-white">
          <VectorIcon fill="white" width="10" height="10" />
        </DialogDismiss>
      </nav>
      <ModalHeadings>
        <strong>{t('login.stagePasswordConfirm.heading')}</strong>
        <br />
        {t('login.stagePasswordConfirm.description')}
      </ModalHeadings>

      <fetcher.Form
        action="/bolt/confirm/"
        className="flex size-full flex-col items-start gap-4 bg-black"
        method="post"
      >
        <input type="hidden" value={qsParams.action ?? ''} name="action" />
        <input type="hidden" value={qsParams.lid ?? ''} name="lid" />
        <input
          type="hidden"
          value={qsParams.returnUrl ?? ''}
          name="returnUrl"
        />
        <input type="hidden" value={qsParams.guest_id ?? ''} name="guestId" />
        <input type="hidden" value={qsParams.token ?? ''} name="token" />
        <input
          type="hidden"
          value={qsParams.from_nudge ?? ''}
          name="fromNudge"
        />
        <input type="hidden" value={qsParams.client ?? ''} name="client" />
        <input type="hidden" value={qsParams.medium ?? ''} name="medium" />
        <input type="hidden" value={qsParams.source ?? ''} name="source" />
        <TextInput
          className="h-12 w-full rounded-md p-4"
          error={
            errors?.password
              ? t('login.stagePasswordConfirm.passwordFormatError')
              : undefined
          }
          id="confirm-password"
          name="password"
          placeholder={t('login.password')}
          type="password"
        />
        <TextInput
          className="h-12 w-full rounded-md p-4"
          error={
            errors?.mismatch
              ? t('login.stagePasswordConfirm.passwordMismatchError')
              : undefined
          }
          id="confirm-confirm"
          name="confirmedPassword"
          placeholder={t('login.confirmPassword')}
          type="password"
        />
        <Button variant="green-human" disabled={!idle}>
          {idle ? t('login.confirmPassword') : t('login.confirming')}
        </Button>
        <ErrorMessage
          isActive={Boolean(errors?.conflict)}
          text={t(
            'login.stagePasswordConfirm.alreadyConfirmedRegistrationError'
          )}
        />
        <ErrorMessage
          isActive={Boolean(errors?.authentication)}
          text={t('login.stagePasswordConfirm.confirmingPasswordError')}
        />
      </fetcher.Form>
    </Dialog>
  ) : null;
};
